import React, { useCallback, useEffect, useState } from "react";

import {
  AlertCircle,
  CheckCircle,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Package,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { ListSkeleton } from "~/components/ui/skeletons";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

interface DispatchGuide {
  id: string;
  sales_request_id: string;
  document_type: string;
  document_url: string | null;
  filename: string | null;
  external_id: number | null;
  bsale_number: string | null;
  url_public_view: string | null;
  url_pdf: string | null;
  url_xml: string | null;
  token: string | null;
  generation_date: number | null;
  state: number | null;
  emission_date: number | null;
  sii_declared: boolean | null;
  sii_declaration_date: string | null;
  created_at: string;
  response_data: any;
}

interface DispatchGuidesSectionProps {
  salesRequestId: string;
  orderStatus: string;
  selectedOrder: SalesRequest;
  onOrderStatusChange?: () => void;
}

export const DispatchGuidesSection: React.FC<DispatchGuidesSectionProps> = ({
  salesRequestId,
  orderStatus,
  selectedOrder,
  onOrderStatusChange,
}) => {
  const [dispatchGuides, setDispatchGuides] = useState<DispatchGuide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [declaringToSii, setDeclaringToSii] = useState<string | null>(null);
  const [isTestClient, setIsTestClient] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [guideToDelete, setGuideToDelete] = useState<DispatchGuide | null>(null);

  const { role } = useProfileStore();
  const isAdmin = role === "admin" || role === "superadmin";

  // Check if client is a test client
  useEffect(() => {
    const checkClientTestStatus = async () => {
      try {
        const { data: client, error } = await supabase
          .from("clients")
          .select("is_test")
          .eq("id", selectedOrder.clientId)
          .single();

        if (!error && client) {
          setIsTestClient(!!client.is_test);
        }
      } catch (error) {
        console.error("Error checking client test status:", error);
      }
    };

    checkClientTestStatus();
  }, [selectedOrder.clientId]);

  // Declare dispatch guide to SII
  const declareToSii = async (document: DispatchGuide) => {
    if (!document.id) return;

    setDeclaringToSii(document.id);

    try {
      const { data, error } = await supabase.functions.invoke(
        "declare-guia-sii",
        {
          body: { document_id: document.id },
        }
      );

      if (error) {
        console.error("Error declaring to SII:", error);
        toast({
          title: "Error",
          description: error.message || "No se pudo declarar al SII",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "¡Declarado al SII!",
        description: `Guía de despacho #${document.bsale_number} declarada exitosamente al SII`,
        variant: "default",
      });

      // Refresh the dispatch guides to show updated status
      await fetchDispatchGuides();
    } catch (error) {
      console.error("Error declaring to SII:", error);
      toast({
        title: "Error",
        description: "Ocurrió un error al declarar al SII",
        variant: "destructive",
      });
    } finally {
      setDeclaringToSii(null);
    }
  };

  // Delete a draft dispatch guide (admin/superadmin only). Deletes the BSale draft, restores the
  // físico the dispatch rebajó, and reverts the order so it can be re-dispatched (full undo).
  const handleDeleteGuide = async () => {
    const guide = guideToDelete;
    if (!guide) return;

    setDeletingId(guide.id);
    try {
      const { data, error } = await supabase.functions.invoke(
        "delete-guia-despacho",
        { body: { document_id: guide.id } }
      );

      if (error) {
        // Surface the edge function's Spanish message (lives in the error response body).
        let message = error.message || "No se pudo eliminar la guía";
        try {
          // deno-lint-ignore no-explicit-any
          const ctx = (error as any).context;
          if (ctx && typeof ctx.json === "function") {
            const bodyJson = await ctx.json();
            message = bodyJson?.message || bodyJson?.error || message;
          }
        } catch {
          /* keep default message */
        }
        toast({
          title: "No se pudo eliminar la guía",
          description: message,
          variant: "destructive",
        });
        return;
      }

      const restoredBoxes = Array.isArray(data?.stockRestore?.restored)
        ? data.stockRestore.restored.reduce(
            // deno-lint-ignore no-explicit-any
            (sum: number, line: any) => sum + (Number(line?.quantity) || 0),
            0
          )
        : 0;

      toast({
        title: "Guía eliminada",
        description:
          restoredBoxes > 0
            ? `La guía fue eliminada en BSale. Se repusieron ${restoredBoxes} caja(s) al stock y la nota volvió a estado aprobado.`
            : "La guía de despacho fue eliminada en BSale.",
      });

      setGuideToDelete(null);
      await fetchDispatchGuides();
      onOrderStatusChange?.();
    } catch (err) {
      console.error("Error deleting dispatch guide:", err);
      toast({
        title: "Error",
        description: "Ocurrió un error al eliminar la guía de despacho",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Fetch dispatch guides from the database
  const fetchDispatchGuides = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("sales_request_documents")
        .select("*")
        .eq("sales_request_id", salesRequestId)
        .eq("document_type", "guia_de_despacho")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (fetchError) {
        console.error("Error fetching dispatch guides:", fetchError);
        setError("No se pudieron cargar las guías de despacho");
        toast({
          title: "Error",
          description: "No se pudieron cargar las guías de despacho",
          variant: "destructive",
        });
      } else {
        setDispatchGuides(data || []);
      }
    } catch (err) {
      console.error("Error fetching dispatch guides:", err);
      setError("No se pudieron cargar las guías de despacho");
      toast({
        title: "Error",
        description: "No se pudieron cargar las guías de despacho",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [salesRequestId]);

  // Load dispatch guides on component mount
  useEffect(() => {
    fetchDispatchGuides();
  }, [fetchDispatchGuides]);

  // Function to open the document URL in a new tab
  const viewDocument = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Function to get status badge
  const getStatusBadge = (state: number | null) => {
    switch (state) {
      case 0:
        return (
          <Badge
            variant="outline"
            className="border-yellow-200 bg-yellow-50 text-yellow-700"
          >
            Borrador
          </Badge>
        );
      case 1:
        return (
          <Badge
            variant="outline"
            className="border-green-200 bg-green-50 text-green-700"
          >
            Vigente
          </Badge>
        );
      case 2:
        return (
          <Badge
            variant="outline"
            className="border-red-200 bg-red-50 text-red-700"
          >
            Anulado
          </Badge>
        );
      default:
        return <Badge variant="outline">Sin estado</Badge>;
    }
  };

  // Function to get SII declaration badge
  const getSiiStatusBadge = (guide: DispatchGuide) => {
    if (guide.sii_declared) {
      return (
        <Badge
          variant="outline"
          className="border-blue-200 bg-blue-50 text-blue-700"
        >
          <CheckCircle className="mr-1 h-3 w-3" />
          Declarado SII
        </Badge>
      );
    } else {
      return (
        <Badge
          variant="outline"
          className="border-orange-200 bg-orange-50 text-orange-700"
        >
          <AlertCircle className="mr-1 h-3 w-3" />
          Borrador
        </Badge>
      );
    }
  };

  // Function to format dates
  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("es-CL");
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
          <h3 className="flex items-center text-lg font-semibold text-gray-800">
            <Package className="mr-2 h-5 w-5 text-blue-600" />
            Guías de Despacho
          </h3>
        </div>
        <div className="p-4">
          <ListSkeleton rows={2} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
          <h3 className="flex items-center text-lg font-semibold text-gray-800">
            <Package className="mr-2 h-5 w-5 text-blue-600" />
            Guías de Despacho
          </h3>
        </div>
        <div className="p-4">
          <div className="py-8 text-center text-red-500">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
        <h3 className="flex items-center text-lg font-semibold text-gray-800">
          <Package className="mr-2 h-5 w-5 text-blue-600" />
          Guías de Despacho
          {dispatchGuides.length > 0 && (
            <Badge variant="outline" className="ml-2">
              {dispatchGuides.length}
            </Badge>
          )}
        </h3>
      </div>

      <div className="p-4">
        {dispatchGuides.length > 0 ? (
          <div className="space-y-3">
            {dispatchGuides.map((guide) => (
              <div
                key={guide.id}
                className="flex items-center gap-3 rounded-lg border bg-gray-50 p-4 hover:bg-gray-100"
              >
                <FileText className="h-8 w-8 flex-shrink-0 text-blue-500" />

                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <div className="text-sm font-medium">
                      {guide.filename ||
                        `Guía de Despacho #${guide.bsale_number || guide.external_id}`}
                    </div>
                    {getStatusBadge(guide.state)}
                    {getSiiStatusBadge(guide)}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 md:grid-cols-4">
                    {guide.bsale_number && (
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-700">N°</span>
                        <span>{guide.bsale_number}</span>
                      </div>
                    )}
                    {guide.emission_date && (
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-700">Fecha</span>
                        <span>{formatDate(guide.emission_date)}</span>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-700">Creado</span>
                      <span>
                        {new Date(guide.created_at).toLocaleDateString("es-CL")}
                      </span>
                    </div>
                    {guide.sii_declaration_date && (
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-700">
                          Declarado SII
                        </span>
                        <span>
                          {new Date(
                            guide.sii_declaration_date
                          ).toLocaleDateString("es-CL")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-shrink-0 gap-1">
                  {/* SII Declaration Button */}
                  {!guide.sii_declared && !isTestClient && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 border-blue-200 px-2 text-xs text-blue-600 hover:bg-blue-50"
                      onClick={() => declareToSii(guide)}
                      disabled={declaringToSii === guide.id}
                      title="Declarar al SII"
                    >
                      {declaringToSii === guide.id ? (
                        <>
                          <div className="mr-1 h-3 w-3 animate-spin rounded-full border border-blue-600 border-t-transparent"></div>
                          SII
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Declarar SII
                        </>
                      )}
                    </Button>
                  )}

                  {guide.url_pdf && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      onClick={() => viewDocument(guide.url_pdf!)}
                      title="Ver PDF"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}

                  {guide.url_public_view && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                      onClick={() => viewDocument(guide.url_public_view!)}
                      title="Ver en BSale"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}

                  {guide.document_url &&
                    guide.document_url !== guide.url_pdf &&
                    guide.document_url !== guide.url_public_view && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                        onClick={() => viewDocument(guide.document_url!)}
                        title="Ver documento"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    )}

                  {/* Delete (admin/superadmin only, drafts not yet declared to SII) */}
                  {isAdmin && !guide.sii_declared && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      onClick={() => setGuideToDelete(guide)}
                      disabled={deletingId === guide.id}
                      title="Eliminar guía (borrador)"
                    >
                      {deletingId === guide.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <Package className="mx-auto mb-2 h-12 w-12 text-gray-300" />
            <p>No hay guías de despacho generadas</p>
            <p className="mt-1 text-xs text-gray-400">
              Las guías de despacho aparecerán aquí una vez creadas
            </p>
          </div>
        )}
      </div>
      </div>

      <AlertDialog
        open={!!guideToDelete}
        onOpenChange={(open) => {
          if (!open && !deletingId) setGuideToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar guía de despacho?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el borrador de la guía{" "}
              <span className="font-medium">
                {guideToDelete?.bsale_number
                  ? `N° ${guideToDelete.bsale_number}`
                  : (guideToDelete?.filename ?? "")}
              </span>{" "}
              en BSale. Se repondrá el stock que la guía rebajó y la nota de
              venta volverá a estado aprobado para poder re-despacharla. Esta
              acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteGuide();
              }}
              disabled={!!deletingId}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deletingId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando…
                </>
              ) : (
                "Eliminar guía"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
